import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Mail,
  FileText,
  Activity,
  Search,
  ArrowUpRight,
  Inbox,
  Download,
  Send,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in-review", label: "In review" },
  { value: "replied", label: "Replied" },
  { value: "archived", label: "Archived" },
];

export default function AdminDashboardPage() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("contacts");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drawerStatus, setDrawerStatus] = useState("new");
  const [drawerNotes, setDrawerNotes] = useState("");
  const [drawerForwardTo, setDrawerForwardTo] = useState("");
  const [drawerBusy, setDrawerBusy] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/contacts"),
        api.get("/admin/diagnostic-leads"),
      ]);
      setStats(s.data);
      setContacts(c.data);
      setLeads(l.data);
    } catch (e) {
      toast.error("Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openDrawer = (contact) => {
    setSelected(contact);
    setDrawerStatus(contact.status || "new");
    setDrawerNotes(contact.notes || "");
    setDrawerForwardTo("");
  };

  const closeDrawer = () => {
    if (drawerBusy) return;
    setSelected(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const filteredContacts = useMemo(() => {
    if (!q.trim()) return contacts;
    const s = q.toLowerCase();
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(s) ||
        c.email.toLowerCase().includes(s) ||
        (c.company || "").toLowerCase().includes(s) ||
        (c.message || "").toLowerCase().includes(s) ||
        (c.status || "").toLowerCase().includes(s)
    );
  }, [contacts, q]);

  const filteredLeads = useMemo(() => {
    if (!q.trim()) return leads;
    const s = q.toLowerCase();
    return leads.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(s) ||
        c.email.toLowerCase().includes(s) ||
        (c.company || "").toLowerCase().includes(s)
    );
  }, [leads, q]);

  // CSV export — fetch with auth header then trigger download
  const exportCsv = async (kind) => {
    try {
      const url =
        kind === "contacts"
          ? `${API}/admin/export/contacts.csv`
          : `${API}/admin/export/diagnostic-leads.csv`;
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        toast.error(`Export failed (${resp.status})`);
        return;
      }
      const blob = await resp.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download =
        kind === "contacts"
          ? "axiom-ai-contacts.csv"
          : "axiom-ai-diagnostic-leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const saveDrawer = async () => {
    if (!selected) return;
    setDrawerBusy(true);
    try {
      const { data } = await api.patch(`/admin/contacts/${selected.id}`, {
        status: drawerStatus,
        notes: drawerNotes,
      });
      setContacts((prev) =>
        prev.map((c) => (c.id === data.id ? data : c))
      );
      setSelected(data);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setDrawerBusy(false);
    }
  };

  const forwardEmail = async (toMe = true) => {
    if (!selected) return;
    if (!stats?.email_enabled) {
      toast.error(
        "Email is disabled. Add RESEND_API_KEY to backend .env to enable."
      );
      return;
    }
    const body = {
      note: drawerNotes,
      ...(toMe ? {} : { recipient_email: drawerForwardTo }),
    };
    setDrawerBusy(true);
    try {
      const { data } = await api.post(
        `/admin/contacts/${selected.id}/forward`,
        body
      );
      toast.success(`Forwarded to ${data.recipient}`);
      // refresh contact
      await loadAll();
      setSelected((prev) =>
        prev
          ? { ...prev, status: "replied" }
          : prev
      );
    } catch (e) {
      const msg =
        e.response?.data?.detail || "Failed to send. Check backend logs.";
      toast.error(typeof msg === "string" ? msg : "Failed to send");
    } finally {
      setDrawerBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-axiom-bg" data-testid="admin-dashboard-page">
      <header className="sticky top-0 z-40 bg-axiom-bg/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="signal-dot animate-pulse-signal" />
            <span className="font-display text-lg font-bold tracking-tight text-axiom-text">
              AXIOM-AI
            </span>
            <span className="num-label hidden sm:inline ml-2 pl-2 border-l border-white/10">
              CONTROL ROOM
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs font-mono text-axiom-muted">
              {user?.email}
            </span>
            <button
              data-testid="admin-refresh"
              onClick={loadAll}
              className="text-axiom-muted hover:text-axiom-text p-2 border border-white/10 hover:border-white/25 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="btn-axiom-outline text-xs"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="num-label">ADMIN / OVERVIEW</div>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl text-axiom-text font-bold tracking-tight">
              Incoming Signals
            </h1>
            <p className="mt-2 text-axiom-muted text-sm">
              Strategic review requests and diagnostic leads, sorted newest first.
            </p>
          </div>
          {stats && (
            <div
              data-testid="email-status"
              className={`flex items-center gap-2 px-3 py-2 border ${
                stats.email_enabled
                  ? "border-axiom-accent/40 text-axiom-accent"
                  : "border-white/10 text-axiom-muted"
              } text-xs font-mono`}
            >
              <span
                className={`signal-dot ${
                  stats.email_enabled ? "animate-pulse-signal" : "opacity-30"
                }`}
                style={{ width: 6, height: 6 }}
              />
              EMAIL · {stats.email_enabled ? "ARMED" : "DISARMED"}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          <StatCard
            label="Contact requests"
            value={stats?.contacts_total ?? "–"}
            sub={`+${stats?.contacts_last_7d ?? 0} in last 7d`}
            icon={Mail}
            testid="stat-contacts"
          />
          <StatCard
            label="Diagnostic leads"
            value={stats?.leads_total ?? "–"}
            sub={`+${stats?.leads_last_7d ?? 0} in last 7d`}
            icon={FileText}
            testid="stat-leads"
          />
          <StatCard
            label="Signal health"
            value="LIVE"
            sub="API · DB · Auth OK"
            icon={Activity}
            testid="stat-health"
            accent
          />
          <StatCard
            label="Total inbox"
            value={(stats?.contacts_total ?? 0) + (stats?.leads_total ?? 0)}
            sub="Across both queues"
            icon={Inbox}
            testid="stat-unread"
          />
        </div>

        {/* Tabs + search + export */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
          <div className="flex items-center border border-white/10 divide-x divide-white/10 self-start">
            <TabBtn
              active={tab === "contacts"}
              onClick={() => setTab("contacts")}
              testid="tab-contacts"
            >
              Contacts · {filteredContacts.length}
            </TabBtn>
            <TabBtn
              active={tab === "leads"}
              onClick={() => setTab("leads")}
              testid="tab-leads"
            >
              Diagnostic Leads · {filteredLeads.length}
            </TabBtn>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-axiom-dim"
              />
              <input
                data-testid="admin-search"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className="bg-axiom-surface border border-white/10 pl-9 pr-4 py-2 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none transition-colors w-full sm:w-72"
              />
            </div>
            <button
              data-testid="admin-export-csv"
              onClick={() => exportCsv(tab)}
              className="btn-axiom-outline text-xs"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="border border-white/10">
          {tab === "contacts" ? (
            <ContactsTable items={filteredContacts} onSelect={openDrawer} />
          ) : (
            <LeadsTable items={filteredLeads} />
          )}
        </div>
      </main>

      {selected && (
        <div
          data-testid="contact-detail-drawer"
          className="fixed inset-0 z-50 flex"
          onClick={closeDrawer}
        >
          <div className="flex-1 bg-axiom-bg/60 backdrop-blur-sm" />
          <div
            className="w-full max-w-xl bg-axiom-surface border-l border-white/10 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="num-label">CONTACT.RECORD</span>
                <button
                  data-testid="drawer-close"
                  className="text-axiom-muted hover:text-axiom-text"
                  onClick={closeDrawer}
                  disabled={drawerBusy}
                >
                  ✕
                </button>
              </div>
              <h2 className="font-display text-2xl text-axiom-text font-bold">
                {selected.name}
              </h2>
              <a
                href={`mailto:${selected.email}`}
                className="text-axiom-accent text-sm inline-flex items-center gap-1 mt-1 hover:underline"
              >
                {selected.email} <ArrowUpRight size={12} />
              </a>
              <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                <KV label="COMPANY" value={selected.company || "—"} />
                <KV label="ROLE" value={selected.role || "—"} />
                <KV label="INTEREST" value={selected.interest} />
                <KV label="RECEIVED" value={fmtDate(selected.created_at)} />
              </div>
              <div className="mt-6 border-t border-white/10 pt-6">
                <div className="num-label mb-3">MESSAGE</div>
                <p className="text-sm text-axiom-text whitespace-pre-wrap leading-relaxed">
                  {selected.message}
                </p>
              </div>

              {/* Status */}
              <div className="mt-6 border-t border-white/10 pt-6">
                <div className="num-label mb-3">STATUS</div>
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="drawer-status"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.value}
                      data-testid={`drawer-status-${s.value}`}
                      onClick={() => setDrawerStatus(s.value)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider font-mono border transition-colors ${
                        drawerStatus === s.value
                          ? "border-axiom-accent bg-axiom-accent/10 text-axiom-accent"
                          : "border-white/10 text-axiom-muted hover:border-white/25"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="mt-6 border-t border-white/10 pt-6">
                <div className="num-label mb-2">NOTES</div>
                <textarea
                  data-testid="drawer-notes"
                  rows={4}
                  value={drawerNotes}
                  onChange={(e) => setDrawerNotes(e.target.value)}
                  className="w-full bg-axiom-bg border border-white/10 px-3 py-2 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none resize-none"
                  placeholder="Add follow-up notes, next steps, internal context..."
                />
                <button
                  data-testid="drawer-save"
                  onClick={saveDrawer}
                  disabled={drawerBusy}
                  className="mt-3 btn-axiom-outline text-xs disabled:opacity-60"
                >
                  {drawerBusy ? (
                    <>
                      <Loader2 size={12} className="animate-spin" /> Saving
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={12} /> Save
                    </>
                  )}
                </button>
              </div>

              {/* Forward */}
              <div className="mt-6 border-t border-white/10 pt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="num-label">FORWARD / EMAIL ACTION</div>
                  {!stats?.email_enabled && (
                    <span className="num-label text-axiom-dim">
                      RESEND_API_KEY MISSING
                    </span>
                  )}
                </div>
                <p className="text-xs text-axiom-muted leading-relaxed">
                  Send this submission as a formatted email — either to your
                  admin inbox or to a custom address. Marks the record as{" "}
                  <span className="text-axiom-accent">replied</span>.
                </p>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    data-testid="drawer-forward-me"
                    onClick={() => forwardEmail(true)}
                    disabled={drawerBusy}
                    className="btn-axiom text-xs justify-center disabled:opacity-60 sm:col-span-1"
                  >
                    <Send size={12} /> Forward to me
                  </button>
                  <div className="sm:col-span-2 flex gap-2">
                    <input
                      data-testid="drawer-forward-email"
                      type="email"
                      value={drawerForwardTo}
                      onChange={(e) => setDrawerForwardTo(e.target.value)}
                      placeholder="custom@recipient.com"
                      className="flex-1 bg-axiom-bg border border-white/10 px-3 py-2 text-xs text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none"
                    />
                    <button
                      data-testid="drawer-forward-custom"
                      onClick={() => {
                        if (!drawerForwardTo.trim()) {
                          toast.error("Enter a recipient email.");
                          return;
                        }
                        forwardEmail(false);
                      }}
                      disabled={drawerBusy}
                      className="btn-axiom-outline text-xs whitespace-nowrap disabled:opacity-60"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, testid, accent }) {
  return (
    <div className="bg-axiom-bg p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="num-label">{label}</span>
        <Icon size={14} className={accent ? "text-axiom-accent" : "text-axiom-muted"} />
      </div>
      <div
        className={`mt-4 font-display text-3xl font-bold ${
          accent ? "text-axiom-accent" : "text-axiom-text"
        }`}
      >
        {value}
      </div>
      <div className="num-label mt-1">{sub}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium transition-colors ${
        active
          ? "bg-axiom-accent text-axiom-bg"
          : "bg-axiom-surface text-axiom-muted hover:text-axiom-text"
      }`}
    >
      {children}
    </button>
  );
}

function ContactsTable({ items, onSelect }) {
  if (!items.length)
    return <Empty label="No contact submissions yet." testid="contacts-empty" />;
  return (
    <table className="w-full text-sm" data-testid="contacts-table">
      <thead>
        <tr className="text-left bg-axiom-surface/40 border-b border-white/10 text-xs uppercase tracking-wider text-axiom-dim font-mono">
          <th className="px-4 py-3">Received</th>
          <th className="px-4 py-3">Name</th>
          <th className="px-4 py-3 hidden sm:table-cell">Company</th>
          <th className="px-4 py-3 hidden md:table-cell">Status</th>
          <th className="px-4 py-3 hidden md:table-cell">Interest</th>
          <th className="px-4 py-3">Email</th>
          <th className="px-4 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {items.map((c) => (
          <tr
            key={c.id}
            data-testid={`contact-row-${c.id}`}
            onClick={() => onSelect(c)}
            className="border-b border-white/5 hover:bg-axiom-surface cursor-pointer transition-colors"
          >
            <td className="px-4 py-3 text-axiom-muted font-mono text-xs">
              {fmtDate(c.created_at)}
            </td>
            <td className="px-4 py-3 text-axiom-text">{c.name}</td>
            <td className="px-4 py-3 text-axiom-muted hidden sm:table-cell">
              {c.company || "—"}
            </td>
            <td className="px-4 py-3 hidden md:table-cell">
              <StatusBadge status={c.status} />
            </td>
            <td className="px-4 py-3 hidden md:table-cell">
              <span className="text-[11px] font-mono uppercase border border-axiom-accent/40 text-axiom-accent px-2 py-0.5">
                {c.interest}
              </span>
            </td>
            <td className="px-4 py-3 text-axiom-muted">{c.email}</td>
            <td className="px-4 py-3 text-right text-axiom-accent">
              <ArrowUpRight size={14} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LeadsTable({ items }) {
  if (!items.length)
    return <Empty label="No diagnostic leads yet." testid="leads-empty" />;
  return (
    <table className="w-full text-sm" data-testid="leads-table">
      <thead>
        <tr className="text-left bg-axiom-surface/40 border-b border-white/10 text-xs uppercase tracking-wider text-axiom-dim font-mono">
          <th className="px-4 py-3">Received</th>
          <th className="px-4 py-3">Name</th>
          <th className="px-4 py-3 hidden sm:table-cell">Company</th>
          <th className="px-4 py-3 hidden md:table-cell">Status</th>
          <th className="px-4 py-3">Email</th>
        </tr>
      </thead>
      <tbody>
        {items.map((l) => (
          <tr
            key={l.id}
            data-testid={`lead-row-${l.id}`}
            className="border-b border-white/5 hover:bg-axiom-surface transition-colors"
          >
            <td className="px-4 py-3 text-axiom-muted font-mono text-xs">
              {fmtDate(l.created_at)}
            </td>
            <td className="px-4 py-3 text-axiom-text">{l.name || "—"}</td>
            <td className="px-4 py-3 text-axiom-muted hidden sm:table-cell">
              {l.company || "—"}
            </td>
            <td className="px-4 py-3 hidden md:table-cell">
              <StatusBadge status={l.status} />
            </td>
            <td className="px-4 py-3 text-axiom-muted">
              <a className="hover:text-axiom-accent" href={`mailto:${l.email}`}>
                {l.email}
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({ status }) {
  const s = (status || "new").toLowerCase();
  const map = {
    new: "border-axiom-accent/40 text-axiom-accent",
    "in-review": "border-amber-400/40 text-amber-300",
    replied: "border-emerald-400/40 text-emerald-300",
    archived: "border-white/15 text-axiom-dim",
  };
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-wider border px-2 py-0.5 ${
        map[s] || map.new
      }`}
    >
      {s}
    </span>
  );
}

function Empty({ label, testid }) {
  return (
    <div className="p-12 text-center text-axiom-muted text-sm" data-testid={testid}>
      <Inbox size={28} className="mx-auto text-axiom-dim mb-3" />
      {label}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="num-label">{label}</div>
      <div className="mt-1 text-axiom-text text-sm break-words">{value}</div>
    </div>
  );
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "2-digit",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
